import { useEffect, useRef, useState } from "react";
import { api } from "../api/client";

// GPS contínuo do freteiro durante entrega ativa. Roda enquanto o user
// tem entrega em status 'aceita', 'coletada' ou 'em_transito'. watchPosition envia
// lat/lng pra entregas.lat/lng via PUT /entregas/{id}/localizacao -
// que dispara Supabase Realtime no canal entrega_<id>, e o cliente
// vê o pin do freteiro fluindo no mapa em tempo real.
//
// Por que global em App.js: o freteiro pode aceitar na TelaFreteDisponivel,
// navegar pra TelaRotaFreteiro, depois voltar pra Home pra olhar ganhos.
// Em qualquer tela o GPS continua. Antes vivia dentro da TelaFreteDisponivel
// que dura 1.2s (auto-nav) - resultado: cliente não recebia GPS algum.
//
// Polling 30s pra detectar nova entrega ativa (alinhado com heartbeat).
// Não chama getCurrentPosition manual - watchPosition já cuida.

// 'em_transito' incluso: sem ele o GPS live morria depois da coleta e o
// cliente parava de ver o pin do freteiro andando justamente no trecho final.
const STATUS_ATIVOS = new Set(["aceita", "coletada", "em_transito"]);

// `locadorHabilitado`: quando o LOCADOR faz a entrega em pessoa, o pedido tem
// tipo_entrega='locadora' e a entrega dele é DE PROPÓSITO excluída de
// /entregas/minhas (aquela rota é do app do freteiro). Resultado: o GPS ao vivo
// nunca ligava nessa modalidade e o cliente abria "acompanhar rota" e via um
// mapa vazio (relato do dono, 05/08). Aqui olhamos também as entregas do
// locador — a infra de rastreio (PUT /localizacao → Realtime → cliente) já
// existia e funcionava; só faltava alguém alimentar.
export const useFreteiroGpsEntregaAtiva = (freteiroHabilitado, locadorHabilitado = false) => {
  const [entregaAtivaId, setEntregaAtivaId] = useState(null);
  const watchIdRef = useRef(null);

  // Poll de entrega ativa enquanto freteiro logado e habilitado
  useEffect(() => {
    if (!freteiroHabilitado && !locadorHabilitado) {
      setEntregaAtivaId(null);
      return;
    }
    let cancelado = false;
    const carregar = async () => {
      let ativa = null;
      if (freteiroHabilitado) {
        try {
          const r = await api.fetch("/entregas/minhas");
          if (cancelado) return;
          if (r.ok) {
            const lista = await r.json();
            ativa = (Array.isArray(lista) ? lista : []).find(e => STATUS_ATIVOS.has(e?.status)) || null;
          }
        } catch {}
      }
      // Entrega própria do locador (tipo='locadora')
      if (!ativa && locadorHabilitado) {
        try {
          const r2 = await api.fetch("/entregas/locador/minhas-entregas");
          if (cancelado) return;
          if (r2.ok) {
            const lista2 = await r2.json();
            ativa = (Array.isArray(lista2) ? lista2 : []).find(e => STATUS_ATIVOS.has(e?.status)) || null;
          }
        } catch {}
      }
      if (!cancelado) setEntregaAtivaId(ativa?.id || null);
    };
    carregar();
    const iv = setInterval(carregar, 30000);
    return () => { cancelado = true; clearInterval(iv); };
  }, [freteiroHabilitado, locadorHabilitado]);

  // watchPosition enquanto entrega ativa - cada movimento >~5m dispara
  // PUT /entregas/{id}/localizacao -> Supabase Realtime -> cliente.
  useEffect(() => {
    if (!entregaAtivaId || !navigator.geolocation) return;
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        api.atualizarLocalizacao(entregaAtivaId, pos.coords.latitude, pos.coords.longitude)
          .catch(() => {});
      },
      () => {},
      { enableHighAccuracy: true, maximumAge: 8000, timeout: 15000 }
    );
    watchIdRef.current = watchId;
    return () => {
      if (watchIdRef.current != null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, [entregaAtivaId]);

  return { entregaAtivaId };
};
